"use client"

import {useEffect, useRef} from "react";
import {usePathname} from "@/i18n/navigation";
import {PageName, toPageName} from "@/lib/analytics/page-name";
import {trackBonusPageOpened, trackPageOpened} from "@/lib/analytics/mixpanel-events";

/**
 * `page_opened` (AC-711, spec §9.1). Mounted once in the locale layout.
 *
 * Two things this has to get right:
 *
 * 1. **Fire on navigation, not on re-render.** App Router re-runs effects on every
 *    `searchParams` change, so keying the effect on anything but the pathname
 *    would double-count a page whose query string moved (a filter, a `?ref`). The
 *    pathname ref is the same guard `<MixpanelInitializer/>` uses for identity.
 * 2. **`previous_page` is the page navigated away from**, and is `null` on the
 *    first page of the visit — an explicit null, so the property stays typed in
 *    the Lexicon rather than appearing only on second-and-later pages.
 *
 * `usePathname` comes from `@/i18n/navigation`, so the value is already
 * locale-stripped: `/es/casino/bonuses` and `/casino/bonuses` are one page, not two.
 */
export default function MixpanelPageTracker() {
    const pathname = usePathname();

    const lastPathname = useRef<string | null>(null);
    const previousPage = useRef<PageName | null>(null);

    useEffect(() => {
        if (lastPathname.current === pathname) return;
        lastPathname.current = pathname;

        const page = toPageName(pathname);
        trackPageOpened({page, previousPage: previousPage.current});
        previousPage.current = page;

        // The bonuses page is a route, not a dialog, so its dedicated event (spec
        // §13 — the head of the bonus funnel in AC-14) belongs here rather than in
        // <MixpanelDialogBridge/>.
        if (page === "bonuses") trackBonusPageOpened();
    }, [pathname]);

    return null;
}
